import { Component, OnDestroy, OnInit, ChangeDetectorRef, ViewChildren, QueryList, AfterViewInit, ElementRef } from "@angular/core";
import { applyFroalaAriaFix, stripFroalaWatermark } from '../../utils/froala.utils';
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { Subject ,  Subscription, Observable, of } from "rxjs";

import { NoteService } from '../../services/note.service';
import { StateType } from "../../models/flow.dto";
import { AuthService } from "../../../services/auth.service";
import { NoteType } from "../../models/note-type.enum";
import { Note, objectIsArray } from "../../models/note.model";
import { NoteRelatedItemType } from "../../models/note_relatedItemType.enum";
import { NoteUrgency } from "../../models/note-urgency.enum";
import { RetentionPolicy } from "../../models/retention-policy.model";
import { genId } from "../../../core/services/genId";
import { SidePanelBase } from "../../../common/components/side-panel-base.class";


@Component({
    selector: "review-notes",
    templateUrl: "./review-notes.component.html",
    styleUrls: ['./notes.component.scss'],
    standalone: false
})
export class ReviewNotesComponent extends SidePanelBase implements OnDestroy, OnInit, AfterViewInit {
    static componentName = 'ReviewNotesComponent';

    componentContext: any;
    editing: boolean = false;
    form: FormGroup;
    replyForm: any;
    notesData: any;
    observer: Subject<boolean>;
    public noteText: string;
    public replyText: string;
    reviewNotes: any;
    reviewNotesContext = "$.reviewNotes";
    stateType: StateType = StateType.ReviewForm;
    type: string = "";

    public _notesSubscription: Subscription;
    @ViewChildren('replyForm') replyForms: QueryList<ElementRef>;

    private _froalaOptions: any = null;
    get froalaOptions(): any {
        if (!this._froalaOptions) {
            this._froalaOptions = applyFroalaAriaFix({
                heightMax: 500,
                height: 500,
                heightMin: 150,
                key: this.editorKey,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough'],
                saveInterval: 0,
                typingTimer: 150,
                autofocus: true
            });
        }
        return this._froalaOptions;
    }

    private _replyFroalaOptions: any = null;
    get replyFroalaOptions() : any {
        if (!this._replyFroalaOptions) {
            this._replyFroalaOptions = applyFroalaAriaFix({
                heightMax: 500,
                height: 500,
                heightMin: 150,
                key: this.editorKey,
                toolbarButtons: ['bold', 'italic', 'underline', 'strikeThrough'],
                saveInterval: 0,
                typingTimer: 150,
                autofocus: true
            });
        }
        return this._replyFroalaOptions;
    }


    constructor(
        public authService: AuthService,
        public notesService: NoteService,
        private detector: ChangeDetectorRef) {
        super();
        this.detector.detach();
    }

    ngOnDestroy(): void {
        this._notesSubscription.unsubscribe();
    }

    ngAfterViewInit(): void {
        if (this.replyForms != null) {
            this.replyForms.forEach(r => {
                r.nativeElement.opened = true;
            });
        }
    }

    ngOnInit(): void {
        this._notesSubscription = this.notesService.note$.subscribe(newNote => {
            this.observer = newNote.obs;
            if (!newNote.data) newNote.data = [];
            this.initializeForm();
            if (newNote.reviewNotes) {
                this.reviewNotes = newNote.reviewNotes;
            }
            this.detector.detectChanges();
        });        
    }

    canViewNote(note: any): boolean {
        switch (note.type) {
            case NoteType.General: {
                return true;
            }
            case NoteType.Historical: {
                return this.authService.User && this.authService.User.isInternal;
            }
            case NoteType.Internal: {
                return this.authService.User && this.authService.User.isInternal;
            }
            case NoteType.Administrative: {
                return note.createdById === this.authService.User.id;
            }
            default: {
                alert('Unknown note type');
                return true;
            }
        }
    }

    displayExpander(obj): boolean {
        return objectIsArray(obj) && obj.length > 0;
    }

    isArray(obj): boolean {
        return objectIsArray(obj);
    }
    

    openReplyForm(form: any): void {
        form.opened = true;
        form.isReplying = true;
        form.replyText = "";
        form.componentContext = {
            files: { value: [] }
        };
        form.componentData = {
            enabled: () => true,
            key: "files",
            contextTarget: "$.files",
            validator: () => true,
            config: { useRadioButtons: false }
        }
        this.replyForm = form;
        this.detector.detectChanges();
    }

    reply(parentNote: Note, form: any): void {
        if (this.validateReply()) {
            const reply = this.buildNote();
            reply.files = form.componentContext.files.value;
            reply.text = stripFroalaWatermark(form.replyText);
            reply.type = parentNote.type;
            parentNote.notes.push(reply);
            this.notesService.noteAdded$.next(parentNote);
            this.resetReplyForm(form);
        } else {
            this.detector.detectChanges();
        }
        
    }

    toggleReply(replyForm: any): void {
        replyForm.opened = !replyForm.opened;
        this.detector.detectChanges();
    }


    validate(): void {
        this.form.controls["noteText"].updateValueAndValidity();

        if (this.form.valid) {
            this.noteText = stripFroalaWatermark(this.form.controls["noteText"].value);
        }
    }   

    validateReply(): boolean {
        let validated = this.replyForm && this.replyForm.replyText && this.replyForm.replyText.length > 0;
        if (validated) {
            this.replyText = this.replyForm.replyText;
        } else {
            this.replyForm.isDirty = true;
        }
        return validated;;
    }

    public buildNote(): Note {
        const id = genId();
        return {
            createdBy: this.authService.User.displayName,
            createdById: this.authService.User.id,
            createdDate: new Date(),
            documentType: "Note",
            relatedItemId: this.notesService.workflowId,
            relatedItemType: NoteRelatedItemType.Workflow,
            id: id,
            isDeleted: false,
            noteUrgency: NoteUrgency.LowPriority,
            priority: 3,
            referenceUri: "",
            retentionPolicy: {} as RetentionPolicy,
            type: NoteType.General,
            notes: [],
            files: []
        } as Note;
    }

    public initializeForm(): void {
        this.notesData = {
            enabled: () => true,
            key: 'files',
            contextTarget: '$.files',
            validator: () => true,
            config: { useRadioButtons: false }
        };

        this.componentContext = {
            files: { value: [] }
        };

        let components = {            
            noteText: new FormControl('', [Validators.required]),            
        };

        this.form = new FormGroup(components);        
    }

    public resetReplyForm(form: any): void {
        form.replyText = "";
        form.isReplying = false;
        form.isDirty = false;
        form.opened = true;
        this.replyText = "";
        this.detector.detectChanges();
    }
}